import { QueryKey, useQuery } from '@tanstack/react-query';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { GenreDto } from '@cineamo/api/models/movie/GenreDto.types';
import { MovieAccessibilityDto } from '@cineamo/api/models/movie/MovieAccessibilityDto.types';
import { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { TMDB_IMAGE_RESOLUTION } from '@/constants/tmdb-constants.ts';
import { EventRequestCategory } from '@cineamo/api/constants/EventRequestCategory.enum';

import { getEventRequestById } from '@/utils/api/event-request/event-request-api';
import { getLocalizedContentDescription } from '@cineamo/api/helpers/content-helper';
import { getCrewMembersByJob, getMovieAccessibility, VideoDtoEnhanced } from '@cineamo/api/helpers/movie-helper';
import { getContentById } from '@cineamo/api/resources/content/content-api';
import { getMovieById } from '@cineamo/api/resources/movie/movie-v2-api';

import {
    defaultMovieBackdropPlaceholder,
    defaultMoviePosterPlaceholder,
    getCategorySpecificImage
} from '@/helpers/event-request-helper';
import { getAgeRatingForISO_3166_2 } from '@cineamo/ui/src/helpers/age-rating-helper';
import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { getISO_3166_2 } from '@cineamo/ui/src/helpers/cinema-helper';
import { getShowingDuration } from '@cineamo/ui/src/helpers/showing-helper';

import { getCustomContent } from '@/utils/custom-content';

import { getCachedMovieData } from './useCachedMovieData';
import { getCachedShowingData } from './useCachedShowingData';

const MoviePosterPlaceholder = loadAsset('/images/placeholders/portrait/im-posterFilmPortrait.jpg');
const MovieBackdropPlaceholder = loadAsset('/images/placeholders/landscape/im-posterFilmLandscape.jpg');

type CachedContentDataReturn = {
    data: CachedContentData | undefined;
};

/**
 * Only the flags and the lists are guaranteed. Everything else is derived from a movie, an event
 * request or a custom-content entry, any of which may be absent for a given content — the images in
 * particular are resolved with `usePlaceholder: false` on the movie helpers.
 */
export type CachedContentData = {
    id: ContentDto['id'];
    movieId: MovieDto['id'] | undefined;
    hasCustomContent: boolean;
    hasMovie: boolean;
    trailerUrl: ContentDto['trailerUrl'];
    description: ContentDto['description'];
    name: string;
    title: string | undefined;
    slug: ContentDto['slug'];
    releaseDate: string | null | undefined;
    releaseYear: string | undefined;
    ageRating: string | undefined;
    runtime: number | undefined;
    genres: GenreDto[] | undefined;
    backdropImages: Record<TMDB_IMAGE_RESOLUTION, string | undefined> | undefined;
    backdropImage: string | undefined;
    backdropImageURL_ORIGINAL: string | undefined;
    backdropImageURL_W780: string | undefined;
    backdropBlurDataImage: string | undefined;
    posterImageURL_W780: string | undefined;
    posterBlurDataImage: string | undefined;
    logoImage: string | null | undefined;
    posterImages: Record<TMDB_IMAGE_RESOLUTION, string | undefined> | undefined;
    posterImage: string | undefined;
    showPoster: boolean;
    eventRequestCategory: EventRequestCategory | undefined;
    movieAccessibility: MovieAccessibilityDto | null;
    director: string;
    actors: string[];
    // Entity data for structured data (sameAs / VideoObject) on the detail pages:
    originalTitle: MovieDto['originalTitle'];
    tmdbId: MovieDto['tmdbId'] | undefined;
    imdbId: MovieDto['imdbId'];
    trailer: VideoDtoEnhanced | undefined;
};

export function useCachedContentData<TKey extends QueryKey = QueryKey>(
    options: {
        content?: ContentDto;
        cinema: CinemaDto;
        locale: string;
        showing?: ShowingDto;
        overrideReleaseDate?: string;
    },
    deps?: TKey
): CachedContentDataReturn {
    const content = options.content;
    const { data } = useQuery({
        queryKey: [
            'cached-content-data',
            `contentId-${options.content?.id}`,
            `showingId-${options.showing?.id}`,
            `locale-${options.locale}`,
            ...(deps ?? []).map((dep) => JSON.stringify(dep))
        ],
        queryFn: () => (content ? getCachedContentData({ ...options, content }) : undefined)
    });
    return { data };
}

export async function getCachedContentData(options: {
    showing?: ShowingDto;
    content: ContentDto;
    cinema: CinemaDto;
    locale: string;
    overrideReleaseDate?: string | false;
}): Promise<CachedContentData> {
    const content = options.content.movieId
        ? options.content
        : ((await getContentById(options.content.id)) ?? options.content);
    const showing = options.showing;
    const countryCode = options.cinema?.countryCode;

    // Fetch V2 movie with cinema context for customization resolution
    const cineamoMovie = await getMovieById(content?.movieId, {
        locale: options.locale,
        cinemaId: options.cinema?.id,
        include: 'videos,credits,images,translations,releaseDates,productionCompanies,productionCountries'
    });

    const eventRequest =
        showing?.isRegularShowtime || !showing?.eventRequestId
            ? null
            : await getEventRequestById(showing?.eventRequestId);
    const customContent = getCustomContent(content?.id ?? 0);

    const movie = getCachedMovieData({
        movie: cineamoMovie,
        countryCode,
        locale: options.locale
    });

    const runtimeMinutes =
        movie?.runtime || getShowingDuration(showing, cineamoMovie, options.locale) || content?.duration;

    const showingData = getCachedShowingData(showing, cineamoMovie, countryCode, options.locale);

    const title =
        showing?.name || eventRequest?.name || customContent?.title || movie?.title || content?.name || content?.slug;

    const description =
        customContent?.description ||
        eventRequest?.description ||
        movie?.overview ||
        getLocalizedContentDescription(content, options.locale);

    const posterImageURL_W780 = customContent?.posterUrl || movie?.posterImageURL_W780 || MoviePosterPlaceholder;

    // movie.trailer already resolves the preferred trailer server-side (v2 movie endpoint):
    // cinema customization > locale-matched KinoCheck trailer > TMDB videos
    const trailerUrl = movie?.trailer?.fullUrl ?? content?.trailerUrl;

    const cinemaISO_3166_2 = getISO_3166_2(options.cinema);
    const customAgeRating =
        cineamoMovie && cinemaISO_3166_2 && !movie?.ageRating
            ? await getAgeRatingForISO_3166_2(cineamoMovie.id, cinemaISO_3166_2)
            : undefined;

    const ageRating = movie?.ageRating || customAgeRating?.rating || content?.ageRating;

    // Prepare image data
    const customBackdropImage =
        customContent?.backdropUrl ||
        showingData?.backdropImageURL_W780 ||
        showingData?.posterImageURL_W780 ||
        content?.backdropImageUrl;
    const customPosterImage =
        customContent?.posterUrl ||
        showingData?.posterImageURL_W780 ||
        showingData?.backdropImageURL_W780 ||
        content?.posterImageUrl;

    const movieBackdropImage = movie?.backdropImageURL_W780;
    const moviePosterImage = movie?.posterImageURL_W780;
    const moviePosterBlurDataImage = movie?.posterBlurDataImage;

    // Get fallback images based on event request category
    const category = eventRequest?.category;
    const fallbackBackdropImage = category
        ? getCategorySpecificImage(category, 'backdrop')
        : defaultMovieBackdropPlaceholder;
    const fallbackPosterImage = category ? getCategorySpecificImage(category, 'poster') : defaultMoviePosterPlaceholder;

    const backdropImage = movieBackdropImage || customBackdropImage || fallbackBackdropImage;
    const backdropBlurDataImage =
        customContent?.backdropUrl ||
        showingData?.backdropBlurDataImage ||
        showingData?.posterBlurDataImage ||
        MovieBackdropPlaceholder;

    const posterImage = moviePosterImage || customPosterImage || content?.posterImageUrl || fallbackPosterImage;
    const posterBlurDataImage = moviePosterBlurDataImage;
    const showPoster = !!(moviePosterImage || customPosterImage);

    return {
        id: content?.id,
        name: content?.name,
        movieId: cineamoMovie?.id,
        hasCustomContent: !!customContent,
        hasMovie: !!movie,
        title,
        slug: content?.slug,
        trailerUrl,
        description,

        releaseDate: options.overrideReleaseDate === false ? null : options.overrideReleaseDate || movie?.releaseDate,
        releaseYear: movie?.releaseYear,
        ageRating,
        runtime: runtimeMinutes,
        genres: movie?.genres,
        backdropImages: movie?.backdropImages,
        backdropImageURL_ORIGINAL: movie?.backdropImageURL_ORIGINAL,
        backdropImageURL_W780: movie?.backdropImageURL_W780,
        posterImageURL_W780: posterImageURL_W780,
        // The movie logo is a title treatment of the ORIGINAL movie title, so only use it when the
        // displayed title is that plain movie title — a custom/showing/event name must not be
        // overwritten by the logo (falls back to the title text downstream).
        logoImage: title === movie?.title ? movie?.logoImageURL_W780 : null,

        backdropImage,
        backdropBlurDataImage,
        posterImages: movie?.posterImages,
        posterImage,
        posterBlurDataImage,
        showPoster,
        eventRequestCategory: eventRequest?.category,
        // via the helper: the API delivers the GRETA payload under `_embedded`, so reading
        // `cineamoMovie.movieAccessibility` directly always yielded null (no GRETA badge ever showed)
        movieAccessibility: getMovieAccessibility(cineamoMovie),
        director: getCrewMembersByJob(cineamoMovie, 'Director')
            .map((crewMember) => crewMember.name)
            .join(', '),
        actors:
            cineamoMovie?.credits?.cast
                ?.toSorted((a, b) => (a.order ?? 0) - (b.order ?? 0))
                ?.slice(0, 5)
                ?.map((c) => c.name)
                .filter((name): name is string => name !== undefined) ?? [],
        originalTitle: cineamoMovie?.originalTitle,
        tmdbId: cineamoMovie?.tmdbId,
        imdbId: cineamoMovie?.imdbId,
        trailer: movie?.trailer
    };
}
